<template>
  <div>
    <v-container fill-height fluid class="pb-6">
      <v-row>
        <v-col class="pa-0" xs="12" md="12">
          <v-card class="mx-auto px-2" outlined>
            <v-row>
              <v-col md="6">
                <div class="px-2 pt-2 primary--text font-weight-bold">
                  {{ $vuetify.lang.t("$vuetify.subscription_plans.header_title") }}
                </div>
              </v-col>

              <v-col md="6">
                <v-btn
                  class="float-right text-capitalize"
                  @click="goToCreatePage()"
                  color="primary"
                >
                  <span class="hidden-sm-and-down">
                    <v-icon left>mdi-plus-circle-outline</v-icon>
                    {{ $vuetify.lang.t("$vuetify.subscription_plans.create_plan_btn") }}
                  </span>
                  <span class="hidden-md-and-up">
                    <v-icon>mdi-plus-circle-outline</v-icon>
                  </span>
                </v-btn>
              </v-col>
            </v-row>
            <v-divider></v-divider>

            <v-data-table
              :headers="headers"
              :items="subscriptionPlans"
              :loading="subscriptionPlansLoading"
              :loading-text="$vuetify.lang.t('$vuetify.subscription_plans.messages.loading')"
            >
              <template v-slot:[`item.title`]="{ item }">
                <TruncateTooltip
                  :text="item.title || 'N/A'"
                  :limit="30"
                />
              </template>

              <template v-slot:[`item.createdAt`]="{ item }">
                {{ item.createdAt ? formatDateTime(item.createdAt) : "N/A" }}
              </template>

              <template v-slot:[`item.price`]="{ item }">
                {{ item.price ? formatPrice(item.price, item.currency) : "N/A" }}
              </template>

              <template v-slot:[`item.label_en`]="{ item }">
                <v-chip small color="orange" text-color="white">
                  {{ item.label_en || "N/A" }}
                </v-chip>
              </template>

              <template v-slot:[`item.label_jp`]="{ item }">
                <v-chip small color="orange" text-color="white">
                  {{ item.label_jp || "N/A" }}
                </v-chip>
              </template>

              <template v-slot:[`item.status`]="{ item }">
                <v-chip
                  small
                  :color="item.status === 'active' ? 'success' : 'grey'"
                  dark
                >
                  {{ item.status }}
                </v-chip>
              </template>

              <template v-slot:[`item.features`]="{ item }">
                {{ item.features ? item.features.length : 0 }}
              </template>

              <template v-slot:no-data>
                <div class="py-6 text-center">
                  {{ $vuetify.lang.t("$vuetify.subscription_plans.messages.no_plans_found") }}
                </div>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center align-center">
                  <div>
                    <ButtonGroup
                    :groupType="ACTION_BUTTON_GROUP"
                    :addButton="false"
                    :stopButton="false"
                    :startButton="false"
                    @onDetailsClick="openPlanDetails(item)"
                    @onEditClick="goToEditPage(item)"
                    @onDeleteClick="confirmDelete(item)"
                />
                  </div>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <account-migration-card />

    <free-plan-settings-card />

    <title-match-confirmation-dialog
      v-model="deleteDialog"
      :match-text="toBeDeletedPlanTitle"
      :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
      :message="$vuetify.lang.t('$vuetify.subscription_plans.delete_confirm_message')"
      :input-label="$vuetify.lang.t('$vuetify.subscription_plans.plan_title_input_label')"
      :confirm-button-text="$vuetify.lang.t('$vuetify.common.confirm')"
      :cancel-button-text="$vuetify.lang.t('$vuetify.common.cancel_btn')"
      :confirm-btn-loading="deleting"
      @onConfirmation="deleteSubscription"
      @onCancel="closeDeleteDialog"
    />

    <subscription-plan-details-modal
      v-model="detailsDialog"
      :plan="selectedPlan"
      @edit="goToEditPage"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { ACTION_BUTTON_GROUP } from "@/components/common/buttongroup/_buttongrouptypes";
import ButtonGroup from "@/components/common/buttongroup/ButtonGroup";
import {
  LOAD_ALL_SUBSCRIPTION_PLANS,
  DELETE_SUBSCRIPTION_PLAN,
  ADD_ALERT,
} from "@/store/_actiontypes";
import SubscriptionPlanDetailsModal from "@/components/SubscriptionPlans/SubscriptionPlanDetailsModal.vue";
import TitleMatchConfirmationDialog from "@/components/common/TitleMatchConfirmationDialog.vue";
import AccountMigrationCard from "@/components/SubscriptionPlans/AccountMigrationCard.vue";
import FreePlanSettingsCard from "@/components/SubscriptionPlans/FreePlanSettingsCard.vue";
import { formatDateTime } from "@/helpers/helper";
import TruncateTooltip from "@/components/common/tooltip/TruncateTooltip.vue";
import { formatPrice } from "@/helpers/subscriptionPlan";
import _ from 'lodash';
import getEnv from "@/config/env";

export default {
  name: "SubscriptionPlans",

  components: {
    ButtonGroup,
    SubscriptionPlanDetailsModal,
    TitleMatchConfirmationDialog,
    TruncateTooltip,
    AccountMigrationCard,
    FreePlanSettingsCard,
  },

  data() {
    return {
      ACTION_BUTTON_GROUP,
      detailsDialog: false,
      deleteDialog: false,
      edit: false,
      headers: [
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.title"),
          align: "center",
          value: "title",
        },

        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.price"),
          align: "center",
          value: "price",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.sequence"),
          align: "center",
          value: "sequence",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.total_features"),
          align: "center",
          value: "features",
          sortable: true,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.created_at"),
          align: "center",
          value: "createdAt",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.label_en"),
          align: "center",
          value: "label_en",
          sortable: false,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.label_jp"),
          align: "center",
          value: "label_jp",
          sortable: false,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.status"),
          align: "center",
          value: "status",
          sortable: false,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.actions_header"),
          value: "actions",
          align: "center",
          sortable: false,
        },
      ],
      toBeDeletedPlanId: null,
      toBeDeletedPlanTitle: "",
      selectedPlan: null,
      deleting: false,
    };
  },

  async created() {
    try {

      let subscription = null;

      if (getEnv("VUE_APP_CAPTCHA_ENABLED")) {
        subscription = await this.$recaptcha.execute("subscription");
      }

      await this.loadPlans({ recaptcha_token: subscription });
    } catch (error) {
      console.log("error",error)

      this.addAlert({
        message: this.$vuetify.lang.t(
          "$vuetify.subscription_plans.messages.load_failed"
        ),
        color: "error",
      });

    }
  },

  computed: {
    ...mapGetters("subscription_plans", [
      "subscriptionPlans",
      "subscriptionPlansLoading",
      "subscriptionPlansErrorMessage",
      "subscriptionPlansSuccessMessage",
    ]),
  },

  methods: {
    ...mapActions("subscription_plans", {
      loadPlans: LOAD_ALL_SUBSCRIPTION_PLANS,
      deletePlan: DELETE_SUBSCRIPTION_PLAN,
    }),

    ...mapActions("alert", {
      addAlert: ADD_ALERT,
    }),

    goToCreatePage() {
      this.$router.push({ name: "SubscriptionPlanCreate" });
    },

    goToEditPage(item) {
      if (!item) return;

      this.$router.push({
        name: "SubscriptionPlanEdit",
        params: { id: item.product_id },
      });
    },

    confirmDelete(item) {
      if (!item) return;

      this.toBeDeletedPlanId = item.id;
      this.toBeDeletedPlanTitle = item.title || "";
      this.deleteDialog = true;
    },

    closeDeleteDialog() {
      this.deleteDialog = false;
      this.toBeDeletedPlanId = null;
      this.toBeDeletedPlanTitle = "";
    },

    async deleteSubscription() {
      this.deleting = true;

      try {
        await this.deletePlan(this.toBeDeletedPlanId);

        this.addAlert({
          message: this.subscriptionPlansSuccessMessage
            || this.$vuetify.lang.t("$vuetify.subscription_plans.messages.delete_success"),
          color: "success",
        });

        this.closeDeleteDialog();

      } catch (error) {

        this.addAlert({
          message: this.subscriptionPlansErrorMessage
            || this.$vuetify.lang.t("$vuetify.subscription_plans.messages.delete_failed"),
          color: "error",
        });

      } finally {
        this.deleting = false;
      }
    },

    openPlanDetails(item) {
      if (!item) return;
      this.selectedPlan = _.cloneDeep(item);
      this.detailsDialog = true;
    },
    formatDateTime,
    formatPrice
  },
};
</script>